import Edit16 from '@carbon/icons-react/es/edit/16';
import Hourglass16 from '@carbon/icons-react/es/hourglass/16';
import Redo16 from '@carbon/icons-react/es/redo/16';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Moment } from 'moment';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { User } from 'api/models';
import { Project } from 'api/project';
import { Category } from 'api/timesheets/categories';
import {
  EnabledSourcesConfiguration,
  LockSettingConfiguration,
  TimesheetApprovalType,
} from 'api/timesheets/configuration/configuration';
import { ReactComponent as ApproveOutline16 } from 'assets/svgs/approve-outline.svg';
import { ReactComponent as ApprovalLockedIcon } from 'assets/svgs/approved_lock.svg';
import { ReactComponent as RejectIcon } from 'assets/svgs/reject_red.svg';
import { Menu } from 'components/atoms/dropdown';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { Option } from 'components/atoms/select';
import { FormatTime } from 'components/organisms/timesheets/common/format-time';
import {
  isRowDeleted,
  RowWithTooltip,
} from 'components/organisms/timesheets/common/table-row';
import {
  checkTimeEntryConfig,
  getRowKey,
  showTaskColumn,
} from 'components/organisms/timesheets/common/time-entry-utils';
import {
  BaseCell,
  DateColumnHeaderCell,
  TimesheetTable,
} from 'components/organisms/timesheets/common/timesheet-table';
import { getColumnClassesForTimeCell } from 'components/organisms/timesheets/common/utils';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useNativeActiveOrInvitedUsersQuery } from 'queries/members';
import { DrawerType } from '../my-timesheet/constants';
import {
  TimesheetTableTimeEntry,
  TimesheetWeeklyRow,
  TimesheetWeeklyRowTask,
  TimesheetStatus,
} from '../types';
import { DeleteRow } from './delete-row';
import { EditRow } from './edit-row';
import { EditableCell } from './editable-cell';

const EditIcon = styled(Edit16)`
  margin-right: var(--spacing-03);
`;

const LeftAlignedCell = styled(BaseCell)`
  text-align: left;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;
const Table = styled(TimesheetTable)<{ isMangerFlow: boolean }>`
  .ant-table-tbody > tr.ant-table-row > td:last-child {
    vertical-align: middle;
  }

  .ant-table-tbody > tr > td.holiday-cell:hover {
    background: var(--gray-30);
    background: repeating-linear-gradient(
      -47deg,
      #fff 1%,
      #fff 2%,
      var(--gray-30) 3%,
      #fff 4%,
      #fff 5%
    );
  }
  .ant-table-placeholder {
    display: none;
  }
  .ant-table-tbody > tr > td:first-child,
  .ant-table-tbody > tr > td:nth-child(2) {
    padding: 0;
    vertical-align: middle;
  }
  .ant-table-row {
    > td:last-child svg {
      visibility: ${({ isMangerFlow }) =>
        isMangerFlow ? 'hidden' : 'visible'};
    }
    &:hover {
      > td:last-child svg {
        visibility: visible;
      }
    }
  }
`;

const MoreActionsDropdownStyled = styled(MoreActionsDropdown)`
  width: 50%;
  height: 100%;
`;

const StyledDiv = styled.div`
  width: 50%;
  height: 100%;
`;

const MoreActionsWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: var(--spacing-09);
`;

const StatusWrapper = styled.div<{ color: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ color }) => color};
  width: 50%;
  height: 100%;
`;

const Hourglass = styled(Hourglass16)`
  color: var(--yellow-50);
`;

const Approved = styled(ApproveOutline16)`
  color: var(--green-50);
`;

const RedoIcon = styled(Redo16)`
  margin-right: var(--spacing-03);
`;

const getColumnConfig = ({
  startDate,
  timeEntryCategories,
  editable,
  workingDays,
  currentUser,
  updateTimeEntryInTask,
  deleteTimeEntryInTask,
  addTimeEntryInTask,
  deleteTask,
  unsubmitTask,
  activeColumn,
  setActiveColumn,
  setVisibleDrawer,
  setEditDetails,
  addEntityToProject,
  timesheetEntriesConfig,
  approvalType,
  lockSettings,
  projects,
  projectOptions,
  changeProject,
  addActivityToProject,
  data,
  mandateCategory,
  submittedText,
  approvedText,
  rejectedText,
  lockApprovedText,
}: {
  startDate: Moment;
  timeEntryCategories: Category[];
  workingDays: WorkingDaysConfiguration;
  editable: boolean;
  currentUser: User;
  updateTimeEntryInTask: (
    task: TimesheetWeeklyRowTask,
    entryAt: string,
    timeEntries: TimesheetTableTimeEntry,
  ) => void;
  deleteTimeEntryInTask: (
    task: TimesheetWeeklyRowTask,
    entryAt: string,
    timeEntryId: number,
  ) => void;
  addTimeEntryInTask: (task: TimesheetWeeklyRowTask, entryAt: string) => void;
  deleteTask: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
  ) => void;
  unsubmitTask: (task: TimesheetWeeklyRowTask) => void;
  activeColumn?: string;
  setActiveColumn?: (value: string | undefined) => void;
  setVisibleDrawer: (value: DrawerType) => void;
  setEditDetails: (value: TimesheetWeeklyRow | undefined) => void;
  addEntityToProject: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    entity: any,
  ) => void;
  timesheetEntriesConfig: EnabledSourcesConfiguration;
  approvalType: TimesheetApprovalType;
  lockSettings: LockSettingConfiguration;
  projects: Project[] | undefined;
  projectOptions: Option[];
  changeProject: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    projectToBeAdded: Option,
  ) => void;
  addActivityToProject: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    activityName: string,
  ) => void;
  data: TimesheetWeeklyRow[];
  mandateCategory: boolean;
  submittedText: string;
  approvedText: string;
  rejectedText: string;
  lockApprovedText: string;
}): ColumnsType<any> => {
  const { isOnlyProjectsEnabled } = checkTimeEntryConfig(
    timesheetEntriesConfig,
  );

  const dayColumns = [...Array(7)].map((value, index) => {
    const currentDay = startDate.add(index, 'day');

    return {
      title: <DateColumnHeaderCell date={currentDay} user={currentUser} />,
      width: '6%',
      key: currentDay.format('YYYY-MM-DD'),
      className: getColumnClassesForTimeCell(
        currentDay,
        workingDays,
        activeColumn,
        currentUser,
      ),
      onCell: (record: TimesheetWeeklyRow) => {
        const currentDayData = record.days[currentDay.format('YYYY-MM-DD')];

        return {
          title: '',
          type: 'time-entry-cell',
          timeEntries: currentDayData,
          task: record.task,
          days: record.days,
          day: currentDay,
          timeEntryCategories,
          canEditTimeCell:
            editable &&
            (approvalType === TimesheetApprovalType.Manager
              ? true
              : record.task?.approvalStatus !== TimesheetStatus.Approved),
          timesheetEntriesConfig,
          mandateCategory,
          updateDayEntry: (
            entryAt: string,
            timeEntry: TimesheetTableTimeEntry,
          ) => {
            updateTimeEntryInTask(record.task, entryAt, timeEntry);
          },
          deleteDayEntry: (entryAt: string, timeEntryId: number) => {
            deleteTimeEntryInTask(record.task, entryAt, timeEntryId);
          },
          addDayEntry: (entryAt: string) => {
            addTimeEntryInTask(record.task, entryAt);
          },
          onMouseEnter: () => {
            setActiveColumn?.(currentDay.format('YYYY-MM-DD'));
          },
          onMouseLeave: () => {
            setActiveColumn?.(undefined);
          },
        };
      },
    };
  });

  const menu = (record) => (
    <Menu>
      <Menu.Header>
        <FormattedMessage id="more_actions" />
      </Menu.Header>
      {record.task.entryType === 'ADHOC_ENTRY' && (
        <Menu.Item
          onClick={() => {
            setVisibleDrawer(DrawerType.EditAdhocTaskDrawer);
            if ('activityName' in record.task) {
              setEditDetails(record);
            }
          }}
        >
          <EditIcon />
          <FormattedMessage id="edit" />
        </Menu.Item>
      )}

      {record.task.approvalStatus &&
        !(record.task.approvalStatus === TimesheetStatus.NotSubmitted) && (
          <Menu.Item onClick={() => unsubmitTask(record.task)}>
            <RedoIcon />
            <FormattedMessage id="timesheets.buttons.withdraw_from_approval" />
          </Menu.Item>
        )}
      {(record.task.entryType !== 'ADHOC_ENTRY' &&
        record.task.approvalStatus === TimesheetStatus.NotSubmitted) ||
      !record.task.approvalStatus ? null : (
        <Menu.Divider />
      )}
      {editable && (
        <Menu.ItemDelete
          key="delete"
          onClick={() => deleteTask(record.task, record.days)}
        >
          <FormattedMessage id="delete" />
        </Menu.ItemDelete>
      )}
    </Menu>
  );

  const EntryStatus = ({ status }: { status: TimesheetStatus }) => {
    let color = 'none';

    if (status === TimesheetStatus.NotSubmitted) {
      color = 'none';
    } else if (status === TimesheetStatus.Submitted) {
      color = 'var(--yellow-10)';
    } else if (status === TimesheetStatus.Approved) {
      color = 'var(--green-10)';
    } else if (status === TimesheetStatus.Rejected) {
      color = 'var(--red-10)';
    }

    return (
      <StatusWrapper color={color}>
        {status === TimesheetStatus.Submitted && (
          <Tooltip title={submittedText} mouseEnterDelay={0.8}>
            <Hourglass />
          </Tooltip>
        )}
        {status === TimesheetStatus.Rejected && (
          <Tooltip title={rejectedText} mouseEnterDelay={0.8}>
            <RejectIcon />
          </Tooltip>
        )}
        {status === TimesheetStatus.Approved && !lockSettings.lock.enabled && (
          <Tooltip title={approvedText} mouseEnterDelay={0.8}>
            <Approved />
          </Tooltip>
        )}
        {status === TimesheetStatus.Approved && lockSettings.lock.enabled && (
          <Tooltip title={lockApprovedText} mouseEnterDelay={0.8}>
            <ApprovalLockedIcon />
          </Tooltip>
        )}
      </StatusWrapper>
    );
  };

  return [
    {
      title: (
        <strong>
          <FormattedMessage id="projects.title" />
        </strong>
      ),
      ellipsis: true,
      key: 'projectName',
      width: showTaskColumn(isOnlyProjectsEnabled, data) ? '16%' : '32%',
      onCell: (record: TimesheetWeeklyRow) => {
        return {
          title: '',
          type: 'project-selection-cell',
          task: record.task,
          days: record.days,
          timesheetEntriesConfig,
          projects,
          projectOptions,
          changeProject,
        };
      },
    },
    showTaskColumn(isOnlyProjectsEnabled, data)
      ? {
          title: (
            <strong>
              <FormattedMessage id="work" />
            </strong>
          ),
          ellipsis: true,
          key: 'taskName',
          width: '16%',
          onCell: (record: TimesheetWeeklyRow) => {
            return {
              title: '',
              type: 'task-selection-cell',
              task: record.task,
              days: record.days,
              addEntityToProject,
              timesheetEntriesConfig,
              addActivityToProject,
              projects,
            };
          },
        }
      : {},
    ...dayColumns,
    {
      title: (
        <strong>
          <FormattedMessage id="timesheets.column.total" />
        </strong>
      ),
      width: '6%',
      align: 'left',
      key: 'totalTime',
      render: (_, record: TimesheetWeeklyRow) => {
        const totalMinutes = Object.values(record.days).reduce(
          (total, childEntries) =>
            total +
            childEntries.reduce(
              (subTotal, entry) => subTotal + entry.minutes,
              0,
            ),
          0,
        );

        return (
          <LeftAlignedCell>
            <FormatTime value={totalMinutes} />
          </LeftAlignedCell>
        );
      },
    },
    {
      title: '',
      width: '6%',
      align: 'right',
      key: 'deleteRow',
      render: (_, record: TimesheetWeeklyRow) => {
        const isApprovedLock =
          record.task.approvalStatus === TimesheetStatus.Approved &&
          lockSettings.lock.enabled;

        if (approvalType === TimesheetApprovalType.ProjectOwner) {
          return (
            <MoreActionsWrapper>
              <EntryStatus status={record.task.approvalStatus} />
              {editable && !isApprovedLock ? (
                <MoreActionsDropdownStyled menu={menu(record)} />
              ) : (
                <StyledDiv />
              )}
            </MoreActionsWrapper>
          );
        }

        return editable && !isApprovedLock ? (
          <div>
            {record.task.entryType === 'ADHOC_ENTRY' && (
              <EditRow
                onClick={() => {
                  setVisibleDrawer(DrawerType.EditAdhocTaskDrawer);
                  if ('activityName' in record.task) {
                    setEditDetails(record);
                  }
                }}
              />
            )}
            <DeleteRow
              onDelete={() => deleteTask(record.task, record.days)}
              record={record}
            />
          </div>
        ) : null;
      },
    },
  ];
};

interface Props {
  startDate: Moment;
  data: TimesheetWeeklyRow[];
  timeEntryCategories: Category[];
  workingDays: WorkingDaysConfiguration;
  editable?: boolean;
  updateTimeEntryInTask: (
    task: TimesheetWeeklyRowTask,
    entryAt: string,
    timeEntries: TimesheetTableTimeEntry,
  ) => void;
  deleteTimeEntryInTask: (
    task: TimesheetWeeklyRowTask,
    entryAt: string,
    timeEntryId: number,
  ) => void;
  addTimeEntryInTask: (task: TimesheetWeeklyRowTask, entryAt: string) => void;
  deleteTask: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
  ) => void;
  unsubmitTask: (task: TimesheetWeeklyRowTask) => void;
  pageHeaderHeight: number;
  setVisibleDrawer: (value: DrawerType) => void;
  setEditDetails: (value: TimesheetWeeklyRow | undefined) => void;
  addEntityToProject: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    entity: any,
  ) => void;
  timesheetEntriesConfig: EnabledSourcesConfiguration;
  approvalType: TimesheetApprovalType;
  lockSettings: LockSettingConfiguration;
  projects: Project[] | undefined;
  projectOptions: Option[];
  changeProject: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    projectToBeAdded: Option,
  ) => void;
  addActivityToProject: (
    task: TimesheetWeeklyRowTask,
    days: Record<string, TimesheetTableTimeEntry[]>,
    activityName: string,
  ) => void;
  mandateCategory: boolean;
}

export const WeekViewTable: FC<Props> = ({
  startDate,
  data,
  timeEntryCategories,
  workingDays,
  editable = false,
  updateTimeEntryInTask,
  addTimeEntryInTask,
  deleteTimeEntryInTask,
  deleteTask,
  unsubmitTask,
  pageHeaderHeight,
  setVisibleDrawer,
  setEditDetails,
  addEntityToProject,
  timesheetEntriesConfig,
  approvalType,
  lockSettings,
  projects,
  projectOptions,
  changeProject,
  addActivityToProject,
  mandateCategory,
}) => {
  const [activeColumn, setActiveColumn] = useState<string>();

  const { user } = useUser();
  const { data: nativeActiveUsers } = useNativeActiveOrInvitedUsersQuery();

  const currentUser = nativeActiveUsers?.find(
    (x) => x.userId === user.userId,
  ) as User;

  const submittedText = useTranslation('timesheets.view_types.submitted');
  const approvedText = useTranslation('timesheets.view_types.approved');
  const rejectedText = useTranslation('timesheets.feature.rejected');
  const lockApprovedText = useTranslation('timesheets.feature.approved.lock');

  const columns = getColumnConfig({
    startDate,
    timeEntryCategories,
    workingDays,
    editable,
    currentUser,
    updateTimeEntryInTask,
    addTimeEntryInTask,
    deleteTimeEntryInTask,
    deleteTask,
    unsubmitTask,
    activeColumn,
    setActiveColumn,
    setVisibleDrawer,
    setEditDetails,
    addEntityToProject,
    timesheetEntriesConfig,
    approvalType,
    lockSettings,
    projects,
    projectOptions,
    changeProject,
    addActivityToProject,
    data,
    mandateCategory,
    submittedText,
    approvedText,
    rejectedText,
    lockApprovedText,
  });
  const components = {
    body: {
      cell: EditableCell,
      row: RowWithTooltip,
    },
  };

  const ACTION_BAR_HEIGHT = 48;
  const TABLE_HEADER_HEIGHT = 48;
  const TABLE_SUMMARY_HEIGHT = 32;
  const ACTION_ROW_HEIGHT = 40;
  const FOOTER_ACTION_ROW = 56;
  const TABLE_BODY_HEIGHT =
    pageHeaderHeight +
    ACTION_BAR_HEIGHT +
    TABLE_HEADER_HEIGHT +
    ACTION_ROW_HEIGHT +
    TABLE_SUMMARY_HEIGHT +
    FOOTER_ACTION_ROW;

  return (
    <>
      <Table
        columns={columns}
        dataSource={data}
        components={components}
        pagination={false}
        sticky
        rowKey={(row, index) => {
          const record = row as TimesheetWeeklyRow;

          return getRowKey(record.task, index);
        }}
        rowClassName={(props: any, index) => {
          let classes = '';

          if (isRowDeleted(props.task).isDeleted) {
            classes = classes.concat('in-active');
          }
          if (index === data.length - 1) {
            classes = classes.concat(' scrollToView');
          }

          return classes;
        }}
        onRow={() =>
          ({ tableType: 'entry-table' } as React.HTMLAttributes<HTMLElement>)
        }
        scroll={{
          y: `calc(100vh - ${TABLE_BODY_HEIGHT}px)`,
        }}
        data-cy-table="my-timesheet"
        isMangerFlow={approvalType === TimesheetApprovalType.Manager}
      />
    </>
  );
};
